import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {featureFlags} from '@/config/featureFlags';
import {PROJECT_TAGS} from '@/constants';
import {LINKS} from '@/constants/LinksConstants';
import {PaymentLinkFlows} from '@/dataSource/src/api/payment-link/constants';
import {
  CTypeOptionCheckout,
  FeeModel,
  IFee,
  PaymentLinkModel,
} from '@/dataSource/src/api/payment-link/entities';
import {usePaymentLinkTrackers} from '@/dataSource/src/api/payment-link/trackers';
import {usePaymentLinkRepository} from '@/dataSource/src/api/payment-link/useRepository';
import {mapPaymentLinkToTrackingInformation} from '@/dataSource/src/api/payment-link/utils';
import {IEventBaseDataSchema} from '@/services/segment/models/TrackingManagerEventModel';

const ERROR_PAYMENT_LINK_CODE_NOT_FOUND = 'BankingGetRequestMoneyLinkNotFound';
const maxAmount = 200;

interface PaymentLinkViewModelContextType {
  paymentLinkData?: PaymentLinkModel;
  paymentLinkError?: any;
  feesData?: FeeModel;
  isFetchingPaymentLink: boolean;
  selectedFee?: IFee;
  setSelectedFee: React.Dispatch<React.SetStateAction<IFee>>;
  selectedAmount: number;
  setSelectedAmount: React.Dispatch<React.SetStateAction<number>>;
  disableFees?: boolean;
  isLinkExpired?: boolean;
  isLinkNotFound?: boolean;
  handleFeeOptionSelected: (fee: IFee) => void;
  handleContinueFormCompleted: (fee: IFee) => void;
  setIsSelectTabAmount: React.Dispatch<React.SetStateAction<boolean>>;
  isSelectTabAmount: boolean;
  handleOnCustomAmountClick: () => void;
}

const PaymentLinkViewModelContext = createContext<
  PaymentLinkViewModelContextType | undefined
>(undefined);

export function PaymentLinkViewModelProvider({
  children,
}: {
  children: ReactNode;
}) {
  const {t} = useTranslation();
  const {id: paymentLinkId} = useParams<{id: string}>();

  const {
    paymentLinkData,
    fetchPaymentLink,
    paymentLinkError,
    feesData,
    fetchFees,
    isFetchingPaymentLink,
    paymentLinkErrorCode,
  } = usePaymentLinkRepository(paymentLinkId);
  const [selectedFee, setSelectedFee] = useState<IFee>(undefined);
  const [selectedAmount, setSelectedAmount] = useState<number>(20);
  const [isSelectTabAmount, setIsSelectTabAmount] = useState(false);

  const {
    trackViewRequestMoneyPaymentOptions,
    trackSelectRequestMoneyPaymentOption,
    trackSelectRequestMoneyPaymentInfoConfirm,
    trackSelectChangeAmountPaymentLink,
  } = usePaymentLinkTrackers();

  useEffect(() => {
    if (!featureFlags.isEnabledPaymentLink) {
      window.location.href = LINKS.FEATURES_COMING_SOON;
      return;
    }
    fetchPaymentLink();
  }, [fetchPaymentLink, paymentLinkId]);

  useEffect(() => {
    if (paymentLinkData && !isLinkExpired && !isLinkNotFound) {
      fetchFees();
      const trackingPayload =
        mapPaymentLinkToTrackingInformation(paymentLinkData);
      trackViewRequestMoneyPaymentOptions(
        trackingPayload,
        selectedAmount,
        isSelectTabAmount,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentLinkData]);

  const disableFees = useMemo(() => {
    return selectedAmount === 0 || selectedAmount > maxAmount;
  }, [selectedAmount]);

  const isLinkExpired = useMemo(() => {
    return paymentLinkData?.link_consumed;
  }, [paymentLinkData?.link_consumed]);

  const isLinkNotFound = useMemo(() => {
    return paymentLinkErrorCode === ERROR_PAYMENT_LINK_CODE_NOT_FOUND;
  }, [paymentLinkErrorCode]);

  const mapTrackingPayload = useCallback(
    (prefill: boolean, fee?: IFee): IEventBaseDataSchema => {
      return {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        ...(paymentLinkData.receiver_user?.id && {
          user: {id: paymentLinkData.receiver_user?.id},
        }),
        Fintech: {
          receiver: {
            user: {
              id: paymentLinkData.receiver_user?.id ?? '',
              name: paymentLinkData.receiver_user?.name ?? '',
            },
          },
          transaction: {
            amount: selectedAmount.toString(),

            fee: fee
              ? fee.flat_fee.toString()
              : selectedFee?.flat_fee?.toString(),
            method: fee ? fee.type : selectedFee?.type,
            prefill,
          },
        },
        Banking: {
          receiver: {
            user: {
              id: paymentLinkData.receiver_user?.id ?? '',
              name: paymentLinkData.receiver_user?.name ?? '',
            },
          },
          transaction: {
            amount: selectedAmount.toString(),

            fee: fee
              ? fee.flat_fee.toString()
              : selectedFee?.flat_fee?.toString(),
            method: fee ? fee.type : selectedFee?.type,
            prefill,
          },
        },
        Request: {
          payment_link: {
            id: paymentLinkId,
          },
          description: Boolean(paymentLinkData.note),
          gif: Boolean(paymentLinkData.gif),
          flow: paymentLinkData.single_payment
            ? PaymentLinkFlows.LINK
            : PaymentLinkFlows.QR,
        },
      };
    },
    [
      paymentLinkData,
      paymentLinkId,
      selectedAmount,
      selectedFee?.flat_fee,
      selectedFee?.type,
    ],
  );

  const handleFeeOptionSelected = useCallback(
    (fee: IFee) => {
      if (fee.type !== CTypeOptionCheckout.VENMO) {
        setSelectedFee(fee);
      } else {
        showSnackbar({
          variant: SnackbarType.INFO,
          summary: t('payment_link.venmo_coming_soon.title'),
          message: t('payment_link.venmo_coming_soon.message'),
        });
      }
      const payload = mapTrackingPayload(isSelectTabAmount, fee);
      if (payload) {
        trackSelectRequestMoneyPaymentOption(payload);
      }
    },
    [
      mapTrackingPayload,
      isSelectTabAmount,
      t,
      trackSelectRequestMoneyPaymentOption,
    ],
  );
  const handleContinueFormCompleted = useCallback(
    (fee: IFee) => {
      const payload = mapTrackingPayload(isSelectTabAmount, fee);
      if (payload) {
        trackSelectRequestMoneyPaymentInfoConfirm(payload);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSelectedFee, trackSelectRequestMoneyPaymentOption, mapTrackingPayload],
  );

  const handleOnCustomAmountClick = useCallback(() => {
    showSnackbar({
      variant: SnackbarType.INFO,
      summary: t('payment_link.custom_amount_coming_soon.title'),
      message: t('payment_link.custom_amount_coming_soon.message'),
    });
    trackSelectChangeAmountPaymentLink(
      paymentLinkData?.receiver_user?.id,
      paymentLinkId,
    );
  }, [
    paymentLinkData?.receiver_user?.id,
    paymentLinkId,
    t,
    trackSelectChangeAmountPaymentLink,
  ]);

  return (
    <PaymentLinkViewModelContext.Provider
      value={{
        paymentLinkData,
        paymentLinkError,
        feesData,
        isFetchingPaymentLink,
        selectedFee,
        setSelectedFee,
        selectedAmount,
        setSelectedAmount,
        disableFees,
        isLinkExpired,
        isLinkNotFound,
        handleFeeOptionSelected,
        handleContinueFormCompleted,
        setIsSelectTabAmount,
        isSelectTabAmount,
        handleOnCustomAmountClick,
      }}>
      {children}
    </PaymentLinkViewModelContext.Provider>
  );
}

export const usePaymentLinkViewModelProvider = () => {
  const context = useContext(PaymentLinkViewModelContext);
  if (!context) {
    throw new Error(
      'usePaymentLinkContext must be used within PaymentLinkViewModelProvider',
    );
  }
  return context;
};
